import React, { useState, useEffect } from "react";

export default function OrderCreation() {
  const [form, setForm] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
    urgency: "",
    employee: "",
    status: "On Order",
    notes: "",
    advance: 0,
    total: 0,
    mode: "",
  });

  // Multiple costumes support
  const [costumeList, setCostumeList] = useState([
    { costumeType: "", quantity: 1, size: "", availableSizes: [], customSizeId: null },
  ]);

  const [customers, setCustomers] = useState([]);
  const [employees] = useState([]);
  const [costumes, setCostumes] = useState([]);
  const [loadingCostumes, setLoadingCostumes] = useState(true);

  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);

  const [showFittingOverlay, setShowFittingOverlay] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [currentCostumeIndex, setCurrentCostumeIndex] = useState(null);

  // Custom measurements state
  const [customMeasurements, setCustomMeasurements] = useState({
    bust: "",
    waist: "",
    hip: "",
    length: "",
    shoulder: "",
    sleeve: "",
    notes: ""
  });

  // Customer fittings fetched for selected customer
  const [customerFittings, setCustomerFittings] = useState([]);
  const [selectedFittingProfileId, setSelectedFittingProfileId] = useState(null);

  useEffect(() => {
    fetchCostumes();
  }, []);

  // Fetch all costumes
  const fetchCostumes = async () => {
    setLoadingCostumes(true);
    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/costume/get_costume.php",
        {
          method: "GET",
          headers: {
            "Accept": "application/json",
          },
          mode: "cors",
        }
      );

      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        if (Array.isArray(data)) {
          setCostumes(data);
        } else {
          console.error("Unexpected JSON structure from get_costume.php:", data);
          setCostumes([]);
        }
      } catch (jsonError) {
        console.error("Failed to parse JSON from get_costume.php. Raw response:", raw);
        setCostumes([]);
      }
    } catch (error) {
      console.error("Error fetching costumes:", error);
      setCostumes([]);
    } finally {
      setLoadingCostumes(false);
    }
  };

  // Fetch sizes for a specific costume
  const fetchCostumeSizes = async (costumeId, index) => {
    if (!costumeId) return;

    try {
      const response = await fetch(
        `https://tailor.beezetsoftwaresolution.com/api/costume/get_costume_fittings.php?costume_id=1`,
        {
          method: "GET",
          headers: { "Accept": "application/json" },
          mode: "cors",
        }
      );

      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        const sizesArray = Array.isArray(data) ? data : (data.data && Array.isArray(data.data) ? data.data : []);
        const updated = [...costumeList];
        updated[index].availableSizes = sizesArray;
        setCostumeList(updated);
      } catch (jsonError) {
        console.error("Failed to parse sizes JSON for costume:", costumeId, "raw:", raw);
        const updated = [...costumeList];
        updated[index].availableSizes = [];
        setCostumeList(updated);
      }
    } catch (error) {
      console.error("Error fetching costume sizes:", error);
      const updated = [...costumeList];
      updated[index].availableSizes = [];
      setCostumeList(updated);
    }
  };

  // Fetch customer fittings (profiles) when a customer is selected
  const fetchCustomerFittings = async (customerId) => {
    if (!customerId) {
      setCustomerFittings([]);
      return;
    }

    try {
      const response = await fetch(
        `https://tailor.beezetsoftwaresolution.com/api/costume/get_costume_fittings.php?customer_id=2`,
        {
          method: "GET",
          headers: { "Accept": "application/json" },
          mode: "cors",
        }
      );

      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        if (Array.isArray(data)) {
          setCustomerFittings(data);
        } else if (data.data && Array.isArray(data.data)) {
          setCustomerFittings(data.data);
        } else {
          console.warn("Unexpected customer fittings format:", data);
          setCustomerFittings([]);
        }
      } catch (jsonError) {
        console.error("Failed to parse customer fittings JSON. Raw response:", raw);
        setCustomerFittings([]);
      }
    } catch (error) {
      console.error("Error fetching customer fittings:", error);
      setCustomerFittings([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleCustomerSearch = async (e) => {
    const value = e.target.value;
    setForm({ ...form, customerName: value });

    if (value.trim().length < 3) {
      setFilteredCustomers([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/costumers/get_customers.php",
        { method: "GET", headers: { "Accept": "application/json" }, mode: "cors" }
      );
      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        const customersArray = Array.isArray(data) ? data : (data.data && Array.isArray(data.data) ? data.data : []);
        const filtered = customersArray.filter((c) =>
          c.name.toLowerCase().includes(value.toLowerCase())
        );

        setFilteredCustomers(filtered);
        setShowSuggestions(true);
      } catch (jsonError) {
        console.error("Failed to parse customers response:", raw);
        setFilteredCustomers([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setFilteredCustomers([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setForm({
      ...form,
      customerName: customer.name,
      phoneNumber: customer.phone,
      address: customer.address || form.address,
    });
    setShowSuggestions(false);
    fetchCustomerFittings(customer.id);
  };

  const handleAddNewCustomer = () => {
    setShowAddCustomer(true);
    setShowSuggestions(false);
    setSelectedCustomerId(null);
    setForm({ ...form, customerName: "", phoneNumber: "", address: "" });
    setCustomerFittings([]);
  };

  const handleSaveNewCustomer = async () => {
    if (!form.customerName || !form.phoneNumber) {
      alert("Please fill all required fields!");
      return;
    }

    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/costumers/add_customers.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          body: JSON.stringify({
            name: form.customerName,
            phone: form.phoneNumber,
            address: form.address || "",
          }),
          mode: "cors",
        }
      );

      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        if (data.success) {
          const newCustomer = {
            id: data.id,
            name: form.customerName,
            phone: form.phoneNumber,
            address: form.address || ""
          };
          setCustomers([...customers, newCustomer]);
          setSelectedCustomerId(data.id);
          setShowAddCustomer(false);
          alert("✅ Customer added successfully!");
          fetchCustomerFittings(data.id);
        } else {
          alert("❌ Failed to add customer: " + (data.message || "Unknown error"));
        }
      } catch (jsonError) {
        console.error("Failed to parse add customer response:", raw);
        alert("❌ Unexpected server response while adding customer.");
      }
    } catch (error) {
      console.error("Error adding customer:", error);
      alert("❌ Network error. Please check your connection and try again.");
    }
  };

  const handleEmployeeSearch = async (e) => {
    const value = e.target.value;
    setForm({ ...form, employee: value });

    if (value.trim().length < 3) {
      setFilteredEmployees([]);
      setShowEmployeeSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/employee/get_employees.php",
        { method: "GET", headers: { "Accept": "application/json" }, mode: "cors" }
      );
      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        const employeesArray = Array.isArray(data) ? data : (data.data && Array.isArray(data.data) ? data.data : []);
        const filtered = employeesArray.filter(
          (emp) =>
            emp.name.toLowerCase().includes(value.toLowerCase()) ||
            (emp.employee_id && emp.employee_id.toLowerCase().includes(value.toLowerCase())) ||
            (emp.phone && emp.phone.includes(value))
        );

        setFilteredEmployees(filtered);
        setShowEmployeeSuggestions(true);
      } catch (jsonError) {
        console.error("Failed to parse employees response:", raw);
        setFilteredEmployees([]);
        setShowEmployeeSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setFilteredEmployees([]);
      setShowEmployeeSuggestions(false);
    }
  };

  const handleSelectEmployee = (employee) => {
    setForm({ ...form, employee: `${employee.id} - ${employee.name}` });
    setShowEmployeeSuggestions(false);
  };

  // Multiple Costume Handlers
  const handleCostumeChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...costumeList];
    updated[index][name] = value;

    if (name === "costumeType" && value) {
      updated[index].size = "";
      updated[index].availableSizes = [];
      updated[index].customSizeId = null;
      setCostumeList(updated);
      fetchCostumeSizes(value, index);
    } else {
      setCostumeList(updated);
    }

    if (name === "size" && value === "Custom") {
      setCurrentCostumeIndex(index);
      setSelectedFittingProfileId(null);
      setShowFittingOverlay(true);
    }
  };

  const addCostumeRow = () => {
    setCostumeList([...costumeList, { costumeType: "", quantity: 1, size: "", availableSizes: [], customSizeId: null }]);
  };

  const removeCostumeRow = (index) => {
    const updated = costumeList.filter((_, i) => i !== index);
    setCostumeList(updated);
  };

  // Custom Measurements Handlers
  const handleMeasurementChange = (e) => {
    const { name, value } = e.target;
    setCustomMeasurements({ ...customMeasurements, [name]: value });
  };

  const handleSelectFittingProfile = (profileId) => {
    setSelectedFittingProfileId(profileId);
    if (!profileId) {
      setCustomMeasurements({
        bust: "",
        waist: "",
        hip: "",
        length: "",
        shoulder: "",
        sleeve: "",
        notes: ""
      });
      return;
    }

    const profile = customerFittings.find((p) => String(p.id) === String(profileId));
    if (!profile) return;

    const newValues = {
      bust: "",
      waist: "",
      hip: "",
      length: "",
      shoulder: "",
      sleeve: "",
      notes: profile.profile_name || ""
    };

    (profile.measurements || []).forEach((m) => {
      const name = (m.measurement_name || "").toLowerCase();
      const rawValue = (m.measurement_value || "").toString();
      const matched = rawValue.match(/[\d.]+/);
      const num = matched ? matched[0] : "";

      if (name.includes("bust") || name.includes("chest")) {
        newValues.bust = num;
      } else if (name.includes("waist")) {
        newValues.waist = num;
      } else if (name.includes("hip")) {
        newValues.hip = num;
      } else if (name.includes("length")) {
        newValues.length = num;
      } else if (name.includes("shoulder")) {
        newValues.shoulder = num;
      } else if (name.includes("sleeve")) {
        newValues.sleeve = num;
      }
    });

    setCustomMeasurements(newValues);
  };

 const handleSaveCustomMeasurements = async () => {
  if (!selectedCustomerId) {
    alert("Please select a customer first!");
    return;
  }

  if (currentCostumeIndex === null) {
    alert("No costume selected!");
    return;
  }

  const selectedCostume = costumeList[currentCostumeIndex];
  const costumeIdToSend = selectedCostume.costumeType
    ? parseInt(selectedCostume.costumeType)
    : null;

  // Convert your measurement object to new API format
  const measurements = [];

  if (customMeasurements.bust)
    measurements.push({ name: "Chest", value: customMeasurements.bust });

  if (customMeasurements.waist)
    measurements.push({ name: "Waist", value: customMeasurements.waist });

  if (customMeasurements.hip)
    measurements.push({ name: "Hip", value: customMeasurements.hip });

  if (customMeasurements.length)
    measurements.push({ name: "Length", value: customMeasurements.length });

  if (customMeasurements.shoulder)
    measurements.push({ name: "Shoulder", value: customMeasurements.shoulder });

  if (customMeasurements.sleeve)
    measurements.push({ name: "Sleeve", value: customMeasurements.sleeve });

  if (measurements.length === 0) {
    alert("Please enter at least one measurement!");
    return;
  }

  // Costume name
  const costumeName =
    costumes.find((c) => c.id === parseInt(selectedCostume.costumeType))
      ?.costume_name || "Custom";

  // NEW request format
  const requestData = {
    costume_id: costumeIdToSend,
    customer_id: selectedCustomerId,
    size_name: `Custom Fit for Customer ${selectedCustomerId}`,
    measurements, // already in new format
  };

  try {
    const response = await fetch(
      "https://tailor.beezetsoftwaresolution.com/api/costume/add_costume_fittings.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestData),
        mode: "cors",
      }
    );

    const raw = await response.text();
    const data = JSON.parse(raw);

    if (data.success) {
      alert(
        `✅ Measurements saved successfully! Size ID: ${
          data.standard_size_id || "N/A"
        }`
      );

      const updated = [...costumeList];
      updated[currentCostumeIndex].customSizeId = data.standard_size_id || null;
      updated[currentCostumeIndex].size = `Custom (ID: ${data.standard_size_id})`;
      setCostumeList(updated);

      setShowFittingOverlay(false);
      setCurrentCostumeIndex(null);
      setSelectedFittingProfileId(null);

      // Reset fields
      setCustomMeasurements({
        bust: "",
        waist: "",
        hip: "",
        length: "",
        shoulder: "",
        sleeve: "",
        notes: "",
      });

      fetchCustomerFittings(selectedCustomerId);
    } else {
      alert("❌ Failed: " + (data.message || "Unknown error"));
    }
  } catch (error) {
    console.error("Error saving custom measurements:", error);
    alert("❌ Network error. Please try again.");
  }
};


  const handleCancelMeasurements = () => {
    if (currentCostumeIndex !== null) {
      const updated = [...costumeList];
      updated[currentCostumeIndex].size = "";
      setCostumeList(updated);
    }

    setCustomMeasurements({
      bust: "",
      waist: "",
      hip: "",
      length: "",
      shoulder: "",
      sleeve: "",
      notes: ""
    });
    setShowFittingOverlay(false);
    setCurrentCostumeIndex(null);
    setSelectedFittingProfileId(null);
  };

  // Create Order with multiple costumes
  const handleCreateOrder = async () => {
    if (!selectedCustomerId) {
      alert("Please select or add a customer first!");
      return;
    }

    if (!form.employee || !form.total || !form.advance) {
      alert("Please fill all required fields!");
      return;
    }

    const employeeId = form.employee.split(" - ")[0];

    const orderData = {
      customer_id: selectedCustomerId,
      delivery_date: "2025-11-25",
      trial_date: "2025-11-23",
      urgency: form.urgency || "Normal",
      total_amount: Number(form.total),
      advance_payment: Number(form.advance),
      payment_mode: form.mode || "Cash",
      notes: form.notes || "",
      items: costumeList.map((c) => ({
        costume_id: Number(c.costumeType) || null,
        assigned_employee_id: Number(employeeId) || null,
        standard_size_id: c.customSizeId ? Number(c.customSizeId) : (c.size && !isNaN(Number(c.size)) ? Number(c.size) : 2),
        quantity: Number(c.quantity),
      })),
    };

    try {
      const response = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/order/create_order.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(orderData),
          mode: "cors",
        }
      );

      const raw = await response.text();
      try {
        const data = JSON.parse(raw);
        console.log("Order API Response:", data);

        if (data.success) {
          alert("✅ Order created successfully!");
        } else {
          alert("❌ Failed to create order: " + (data.message || "Unknown error"));
        }
      } catch (jsonError) {
        console.error("Failed to parse create_order response:", raw);
        alert("❌ Unexpected server response while creating order.");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      alert("❌ Network error. Please try again.");
    }
  };

  return (
    <>
      <div className="min-h-screen bg-blue-50 p-4 sm:p-6 text-blue-900 transition-all duration-300 ease-in-out">
        <h1 className="text-2xl font-semibold text-blue-800 mb-4">
          Order Creation
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow p-4 sm:p-6 space-y-6">
            {/* Customer Section */}
            <div>
              <h2 className="font-semibold mb-2 text-blue-800">
                Customer Details
              </h2>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 relative">
                <input
                  className="border p-2 rounded w-full sm:w-3/4 focus:ring-2 focus:ring-blue-700"
                  placeholder="Search or enter customer"
                  name="customerName"
                  value={form.customerName}
                  onChange={handleCustomerSearch}
                  onFocus={() => form.customerName && setShowSuggestions(true)}
                />
                <button
                  onClick={handleAddNewCustomer}
                  className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded text-sm"
                >
                  + Add
                </button>

                {showSuggestions && (
                  <div className="absolute top-12 left-0 bg-white border rounded shadow mt-1 w-full sm:w-3/4 z-10 max-h-40 overflow-y-auto">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map((c) => (
                        <label
                          key={c.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCustomerId === c.id}
                            onChange={() => handleSelectCustomer(c)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span>
                            {c.name} — {c.phone}
                          </span>
                        </label>
                      ))
                    ) : (
                      <div className="p-3 text-sm text-gray-500">
                        No match found.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {showAddCustomer && (
                <div className="border border-blue-300 rounded p-4 mt-3 bg-blue-50">
                  <h3 className="font-medium text-blue-800 mb-2">
                    ➕ Add New Customer
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      className="border p-2 rounded focus:ring-2 focus:ring-blue-700"
                      placeholder="Customer Name"
                      name="customerName"
                      value={form.customerName}
                      onChange={handleChange}
                    />
                    <input
                      className="border p-2 rounded focus:ring-2 focus:ring-blue-700"
                      placeholder="Phone Number"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mt-4">
                    <input
                      className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                      placeholder="Address (Optional)"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex justify-end gap-3 mt-3">
                    <button
                      onClick={() => setShowAddCustomer(false)}
                      className="border border-blue-700 text-blue-700 px-3 py-1 rounded hover:bg-blue-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNewCustomer}
                      className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-1 rounded"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}

              {!showAddCustomer && (
                <div className="mt-3">
                  <input
                    className="border p-2 rounded w-full sm:w-1/2 focus:ring-2 focus:ring-blue-700"
                    placeholder="Phone number"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>

            {/* Costumes Section */}
            <div>
              <h2 className="font-semibold mb-2 text-blue-800">Costumes</h2>
              {costumeList.map((item, index) => (
                <div
                  key={index}
                  className="mb-4 p-3 border rounded-lg bg-gray-50 relative"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <select
                      className="border p-2 rounded focus:ring-2 focus:ring-blue-700"
                      name="costumeType"
                      value={item.costumeType}
                      onChange={(e) => handleCostumeChange(index, e)}
                      disabled={loadingCostumes}
                    >
                      <option value="">
                        {loadingCostumes
                          ? "Loading costumes..."
                          : "Select costume"}
                      </option>
                      {!loadingCostumes &&
                        costumes.map((costume) => (
                          <option key={costume.id} value={costume.id}>
                            {costume.costume_name} - ₹{costume.base_price}
                          </option>
                        ))}
                    </select>
                    <input
                      type="number"
                      className="border p-2 rounded focus:ring-2 focus:ring-blue-700"
                      name="quantity"
                      value={item.quantity}
                      placeholder="Qty"
                      onChange={(e) => handleCostumeChange(index, e)}
                    />
                  </div>

                  <div className="mt-4">
                    <h3 className="font-medium text-blue-700 mb-2">Select Size</h3>
                    <select
                      className="border p-2 rounded w-full sm:w-1/2 focus:ring-2 focus:ring-blue-700"
                      name="size"
                      value={item.size}
                      onChange={(e) => handleCostumeChange(index, e)}
                      disabled={!item.costumeType}
                    >
                      <option value="">
                        {!item.costumeType
                          ? "Select a costume first"
                          : item.availableSizes.length === 0
                            ? "Loading sizes..."
                            : "Choose size"}
                      </option>
                      {item.availableSizes.map((sizeObj) => (
                        <option key={sizeObj.id} value={sizeObj.id}>
                          {sizeObj.size_name}
                        </option>
                      ))}
                      <option value="Custom">Custom Measurements</option>
                    </select>
                  </div>

                  {index > 0 && (
                    <button
                      onClick={() => removeCostumeRow(index)}
                      className="absolute top-3 right-3 text-red-600 text-sm underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={addCostumeRow}
                className="bg-blue-700 text-white px-4 py-2 rounded-lg mt-2"
              >
                + Add More Costumes
              </button>
            </div>

            {/* Employee Section */}
            <div className="relative mt-4">
              <h3 className="font-medium text-blue-700 mb-2">Employee</h3>
              <input
                className="border p-2 rounded w-full sm:w-1/2 focus:ring-2 focus:ring-blue-700"
                placeholder="Search by ID, Name or Phone"
                name="employee"
                value={form.employee}
                onChange={handleEmployeeSearch}
                onFocus={() =>
                  form.employee && setShowEmployeeSuggestions(true)
                }
              />
              {showEmployeeSuggestions && (
                <div className="absolute top-14 left-0 bg-white border rounded shadow mt-1 w-full sm:w-1/2 z-10 max-h-40 overflow-y-auto">
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp) => (
                      <div
                        key={emp.id}
                        onClick={() => handleSelectEmployee(emp)}
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      >
                        {emp.id} — {emp.name} ({emp.phone})
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-gray-500">
                      No employee found.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <select
                className="border p-2 rounded focus:ring-2 focus:ring-blue-700"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option>On Order</option>
                <option>In Progress</option>
                <option>Completed</option>
              </select>
            </div>

            <textarea
              className="border p-2 rounded w-full mt-4 focus:ring-2 focus:ring-blue-700"
              rows="3"
              placeholder="Order notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
            ></textarea>
          </div>

          {/* SUMMARY Section */}
          <div className="bg-white rounded-xl shadow p-4 sm:p-6 h-fit space-y-4">
            <h2 className="font-semibold text-blue-800">Order Summary</h2>
            <div className="text-sm text-blue-700">
              Sizes Selected:{" "}
              {costumeList.map((c) => c.size || "None").join(", ")}
            </div>
            <div className="text-sm text-blue-700">
              Order Status: {form.status}
            </div>

            <input
              type="number"
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
              placeholder="Advance payment"
              name="advance"
              value={form.advance}
              onChange={handleChange}
            />
            <input
              type="number"
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
              placeholder="Total amount"
              name="total"
              value={form.total}
              onChange={handleChange}
            />
            <select
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
              name="mode"
              value={form.mode}
              onChange={handleChange}
            >
              <option value="">Select payment mode</option>
              <option>Cash</option>
              <option>Card</option>
              <option>UPI</option>
            </select>
            <button
              onClick={handleCreateOrder}
              className="bg-blue-700 hover:bg-blue-800 text-white w-full py-2 rounded transition-all"
            >
              Create Order
            </button>
            <button className="border border-blue-700 text-blue-700 w-full py-2 rounded hover:bg-blue-100 transition-all">
              Reset Form
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for Custom Measurement */}
      {showFittingOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
          <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg relative p-6">
            <button
              onClick={handleCancelMeasurements}
              className="absolute top-2 right-3 text-2xl text-gray-600 hover:text-black"
            >
              &times;
            </button>
            <div className="max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">
                Custom Measurements
              </h2>
              <p className="text-sm text-blue-600 mb-4">
                Enter measurements for: <span className="font-semibold">
                  {currentCostumeIndex !== null && costumeList[currentCostumeIndex]?.costumeType
                    ? costumes.find(c => c.id === parseInt(costumeList[currentCostumeIndex].costumeType))?.costume_name || "Selected Costume"
                    : "Costume"}
                </span>
              </p>

              {customerFittings.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Use saved fitting profile
                  </label>
                  <select
                    className="border p-2 rounded w-full sm:w-1/2 focus:ring-2 focus:ring-blue-700"
                    value={selectedFittingProfileId || ""}
                    onChange={(e) => handleSelectFittingProfile(e.target.value || null)}
                  >
                    <option value="">-- Select profile (optional) --</option>
                    {customerFittings.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.profile_name} — Last updated: {p.last_updated}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Bust (inches)
                  </label>
                  <input
                    type="number"
                    name="bust"
                    value={customMeasurements.bust}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter bust size"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Waist (inches)
                  </label>
                  <input
                    type="number"
                    name="waist"
                    value={customMeasurements.waist}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter waist size"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Hip (inches)
                  </label>
                  <input
                    type="number"
                    name="hip"
                    value={customMeasurements.hip}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter hip size"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Length (inches)
                  </label>
                  <input
                    type="number"
                    name="length"
                    value={customMeasurements.length}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter length"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Shoulder (inches)
                  </label>
                  <input
                    type="number"
                    name="shoulder"
                    value={customMeasurements.shoulder}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter shoulder width"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Sleeve (inches)
                  </label>
                  <input
                    type="number"
                    name="sleeve"
                    value={customMeasurements.sleeve}
                    onChange={handleMeasurementChange}
                    className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                    placeholder="Enter sleeve length"
                  />
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={customMeasurements.notes}
                  onChange={handleMeasurementChange}
                  className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-700"
                  rows="3"
                  placeholder="Any special fitting requirements..."
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCancelMeasurements}
                  className="border border-blue-700 text-blue-700 px-4 py-2 rounded hover:bg-blue-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomMeasurements}
                  className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded"
                >
                  Save Measurements
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}